import React, { useState, useMemo } from 'react';
import { colors } from '../theme/tokens';

const STATUS_COLORS = {
  stable: '#2fb65c',
  unstable: '#f4d03f',
  competitor: '#e74c3c',
  lead: '#ff69b4',
  technical: '#9b59b6',
  inactive: '#888888'
};

const STATUS_LABELS = {
  stable: 'Stable Client',
  unstable: 'Unstable Client',
  competitor: 'Buying from Competitor',
  lead: 'New Lead',
  technical: 'Technical Difficulty',
  inactive: 'Inactive'
};

const getTimeAgo = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffDays = Math.floor((now - past) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
};

const ClientList = ({ clients, onClientClick, currentUserId, pendingFilter = null }) => {
  const [sortBy, setSortBy] = useState('lastAction');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRep, setFilterRep] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Apply filter pushed from parent (e.g. stale-leads banner click)
  React.useEffect(() => {
    if (!pendingFilter) return;
    if (pendingFilter.rep !== undefined) setFilterRep(pendingFilter.rep || 'all');
    if (pendingFilter.status !== undefined) setFilterStatus(pendingFilter.status || 'all');
    if (pendingFilter.sortBy !== undefined) setSortBy(pendingFilter.sortBy);
    if (pendingFilter.sortDir !== undefined) setSortDir(pendingFilter.sortDir);
    setSearchQuery('');
    setFiltersOpen(true);
  }, [pendingFilter]);

  // Build unique list of assigned reps for the filter dropdown
  const reps = useMemo(() => {
    const seen = new Set();
    const list = [];
    clients.forEach(c => {
      if (c.claimedBy && c.claimedByName && !seen.has(c.claimedBy)) {
        seen.add(c.claimedBy);
        list.push({ id: c.claimedBy, name: c.claimedByName });
      }
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  // Build unique industry list from clients
  const industries = useMemo(() => (
    [...new Set(clients.map(c => c.industry).filter(Boolean))].sort()
  ), [clients]);

  const handleSortClick = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const sortIcon = (field) => {
    if (sortBy !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedAndFiltered = useMemo(() => {
    let result = clients;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(c => c.clientName.toLowerCase().includes(q));
    }
    if (filterStatus === 'all') {
      // Hide inactive by default — only show when explicitly selected
      result = result.filter(c => c.status !== 'inactive');
    } else {
      result = result.filter(c => c.status === filterStatus);
    }
    if (filterRep !== 'all') {
      if (filterRep === '__unclaimed__') {
        result = result.filter(c => !c.claimedBy);
      } else {
        result = result.filter(c => c.claimedBy?.toString() === filterRep);
      }
    }
    if (filterIndustry !== 'all') result = result.filter(c => c.industry === filterIndustry);

    return [...result].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'lastAction') cmp = new Date(a.lastActionDate) - new Date(b.lastActionDate);
      else if (sortBy === 'name') cmp = a.clientName.localeCompare(b.clientName);
      else if (sortBy === 'status') cmp = a.status.localeCompare(b.status);
      else if (sortBy === 'industry') cmp = (a.industry || '').localeCompare(b.industry || '');
      else if (sortBy === 'salesperson') {
        const aName = a.claimedByName || '';
        const bName = b.claimedByName || '';
        cmp = aName.localeCompare(bName);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [clients, sortBy, sortDir, filterStatus, filterRep, filterIndustry, searchQuery]);

  const SortableHeader = ({ field, children }) => (
    <th
      style={{ ...styles.th, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => handleSortClick(field)}
    >
      {children}{sortIcon(field)}
    </th>
  );

  return (
    <div style={styles.container}>
      {/* Controls */}
      <div style={styles.controls}>
        {/* Search — always visible */}
        <div style={styles.searchGroup}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search company name..."
            style={styles.searchInput}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={styles.searchClearBtn} title="Clear search">✕</button>
          )}
        </div>

        {isMobile ? (
          /* ── Mobile: collapsible filters panel ── */
          <div style={styles.mobileFiltersWrapper}>
            <button
              onClick={() => setFiltersOpen(p => !p)}
              style={{
                ...styles.mobileFiltersToggle,
                background: (filterStatus !== 'all' || filterRep !== 'all' || filterIndustry !== 'all') ? 'rgba(47,182,92,0.20)' : colors.surface,
                borderColor: (filterStatus !== 'all' || filterRep !== 'all' || filterIndustry !== 'all') ? 'rgba(47,182,92,0.55)' : colors.border,
                color:       (filterStatus !== 'all' || filterRep !== 'all' || filterIndustry !== 'all') ? colors.parGreenBright : colors.text,
              }}
            >
              🔽 Filters{(filterStatus !== 'all' || filterRep !== 'all' || filterIndustry !== 'all') ? ' •' : ''}
            </button>

            {filtersOpen && (
              <div style={styles.mobileFiltersPanel}>
                <div style={styles.controlGroup}>
                  <label style={styles.label}>Status:</label>
                  <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.select}>
                    <option value="all">All Statuses (excl. inactive)</option>
                    {Object.entries(STATUS_LABELS).filter(([s]) => s !== 'inactive').map(([s, l]) => (
                      <option key={s} value={s}>{l}</option>
                    ))}
                    <option disabled>──────────────</option>
                    <option value="inactive">Inactive (hidden by default)</option>
                  </select>
                </div>
                <div style={styles.controlGroup}>
                  <label style={styles.label}>Rep:</label>
                  <select value={filterRep} onChange={e => setFilterRep(e.target.value)} style={styles.select}>
                    <option value="all">All Reps</option>
                    <option value="__unclaimed__">Unclaimed</option>
                    {reps.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                {industries.length > 0 && (
                  <div style={styles.controlGroup}>
                    <label style={styles.label}>Industry:</label>
                    <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)} style={styles.select}>
                      <option value="all">All Industries</option>
                      {industries.map(ind => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>
                )}
                {(filterStatus !== 'all' || filterRep !== 'all' || filterIndustry !== 'all') && (
                  <button
                    onClick={() => { setFilterStatus('all'); setFilterRep('all'); setFilterIndustry('all'); }}
                    style={styles.clearFilterBtnSmall}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* ── Desktop: inline filters ── */
          <>
            <div style={styles.controlGroup}>
              <label style={styles.label}>Filter status:</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.select}>
                <option value="all">All Statuses (excl. inactive)</option>
                {Object.entries(STATUS_LABELS).filter(([s]) => s !== 'inactive').map(([s, l]) => (
                  <option key={s} value={s}>{l}</option>
                ))}
                <option disabled>──────────────</option>
                <option value="inactive">Inactive (hidden by default)</option>
              </select>
            </div>
            <div style={styles.controlGroup}>
              <label style={styles.label}>Filter rep:</label>
              <select value={filterRep} onChange={e => setFilterRep(e.target.value)} style={styles.select}>
                <option value="all">All Reps</option>
                <option value="__unclaimed__">Unclaimed</option>
                {reps.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            {industries.length > 0 && (
              <div style={styles.controlGroup}>
                <label style={styles.label}>Filter industry:</label>
                <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)} style={styles.select}>
                  <option value="all">All Industries</option>
                  {industries.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      <div style={styles.stats}>
        <div style={styles.statItem}>Total: <strong>{clients.length}</strong></div>
        <div style={styles.statItem}>Showing: <strong>{sortedAndFiltered.length}</strong></div>
        <div style={styles.statItem}>
          Unclaimed leads: <strong style={{ color: '#ff69b4' }}>
            {clients.filter(c => c.status === 'lead' && !c.claimedBy).length}
          </strong>
        </div>
      </div>

      {isMobile ? (
        <div style={styles.cardGrid}>
          {sortedAndFiltered.map((client) => {
            const isOld = new Date() - new Date(client.lastActionDate) > 30 * 24 * 60 * 60 * 1000;
            return (
              <div key={client._id} style={styles.card} onClick={() => onClientClick(client)}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>{client.clientName}</h3>
                  {client.unreadCount > 0 && (
                    <div style={styles.unreadBadge}>{client.unreadCount}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <div style={{ ...styles.statusBadge, background: STATUS_COLORS[client.status] }}>
                    {STATUS_LABELS[client.status]}
                  </div>
                  {client.industry && (
                    <div style={styles.industryTag}>{client.industry}</div>
                  )}
                </div>
                <div style={styles.cardMeta}>
                  <span style={{ color: colors.textMuted }}>👤 {client.claimedByName || 'Unclaimed'}</span>
                  <span style={{ color: isOld ? '#ff8178' : colors.textMuted }}>{getTimeAgo(client.lastActionDate)}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <SortableHeader field="name">Client Name</SortableHeader>
                <SortableHeader field="status">Status</SortableHeader>
                <SortableHeader field="industry">Industry</SortableHeader>
                <SortableHeader field="salesperson">Assigned To</SortableHeader>
                <SortableHeader field="lastAction">Last Action</SortableHeader>
                <th style={styles.th}>Messages</th>
                <th style={styles.th}>Location</th>
              </tr>
            </thead>
            <tbody>
              {sortedAndFiltered.map((client) => {
                const isOld = new Date() - new Date(client.lastActionDate) > 30 * 24 * 60 * 60 * 1000;
                return (
                  <tr key={client._id} style={styles.tr} onClick={() => onClientClick(client)}>
                    <td style={styles.td}>
                      <div style={styles.nameCell}>
                        {client.clientName}
                        {client.unreadCount > 0 && (
                          <span style={styles.unreadDot} title={`${client.unreadCount} unread`}>
                            {client.unreadCount}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, background: STATUS_COLORS[client.status] }}>
                        {STATUS_LABELS[client.status]}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {client.industry ? (
                        <span style={styles.industryTag}>{client.industry}</span>
                      ) : (
                        <span style={{ color: colors.textFaint, fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {client.claimedByName ? (
                        <span style={styles.repCell}>
                          <span style={styles.repDot} />
                          {client.claimedByName}
                        </span>
                      ) : (
                        <span style={styles.unclaimedCell}>
                          {client.status === 'lead' ? '🔓 Unclaimed' : '—'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td, color: isOld ? '#ff8178' : colors.textMuted, fontWeight: isOld ? '700' : 'normal' }}>
                      {getTimeAgo(client.lastActionDate)}
                    </td>
                    <td style={styles.td}>
                      {client.messages?.length || 0} msg{client.messages?.length !== 1 ? 's' : ''}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.coordinates}>
                        {client.location.lat.toFixed(3)}, {client.location.lng.toFixed(3)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sortedAndFiltered.length === 0 && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <p>No clients found</p>
          {(filterStatus !== 'all' || filterRep !== 'all' || filterIndustry !== 'all') && (
            <button onClick={() => { setFilterStatus('all'); setFilterRep('all'); setFilterIndustry('all'); setSearchQuery(''); }} style={styles.clearFilterBtn}>
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '20px', minHeight: '500px' },
  controls: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-start' },
  mobileFiltersWrapper: { position: 'relative' },
  mobileFiltersToggle: {
    padding: '7px 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
  },
  mobileFiltersPanel: {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 100,
    background: colors.menuBg, borderRadius: '10px', padding: '14px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.45)', border: `1px solid ${colors.border}`,
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
    display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '220px'
  },
  clearFilterBtnSmall: {
    padding: '7px 14px', background: colors.red, color: 'white',
    border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '600', alignSelf: 'flex-start'
  },
  searchGroup: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchInput: {
    padding: '7px 32px 7px 12px', borderRadius: '6px', border: `1px solid ${colors.border}`,
    fontSize: '13px', background: colors.surface, color: colors.text, width: '220px', outline: 'none',
    boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
  },
  searchClearBtn: {
    position: 'absolute', right: '8px', background: 'none', border: 'none',
    cursor: 'pointer', color: colors.textMuted, fontSize: '13px', lineHeight: 1, padding: 0
  },
  controlGroup: { display: 'flex', alignItems: 'center', gap: '8px' },
  label: { fontSize: '13px', fontWeight: '600', color: colors.text },
  select: { padding: '7px 10px', borderRadius: '6px', border: `1px solid ${colors.border}`, fontSize: '13px', cursor: 'pointer', background: colors.surfaceDeep, color: colors.text },
  stats: { display: 'flex', gap: '20px', marginBottom: '16px', padding: '12px 16px', background: colors.surfaceSoft, border: `1px solid ${colors.glassBorder}`, borderRadius: '8px', flexWrap: 'wrap' },
  statItem: { fontSize: '13px', color: colors.textMuted },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', border: `1px solid ${colors.glassBorder}` },
  table: { width: '100%', borderCollapse: 'collapse', background: colors.glass },
  th: { textAlign: 'left', padding: '13px 15px', background: colors.surfaceSoft, fontWeight: '600', fontSize: '13px', color: colors.text, borderBottom: `2px solid ${colors.border}` },
  tr: { borderBottom: `1px solid ${colors.borderSoft}`, cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '13px 15px', fontSize: '13px', color: colors.text },
  nameCell: { display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' },
  unreadDot: { background: '#ff6b35', color: 'white', fontSize: '11px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center' },
  statusBadge: { display: 'inline-block', color: 'white', padding: '4px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' },
  repCell: { display: 'flex', alignItems: 'center', gap: '6px', color: colors.text, fontWeight: '500' },
  repDot: { width: '8px', height: '8px', borderRadius: '50%', background: '#2fb65c', flexShrink: 0 },
  unclaimedCell: { color: colors.textMuted, fontSize: '12px', fontStyle: 'italic' },
  coordinates: { fontFamily: 'monospace', fontSize: '11px', color: colors.textFaint },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px', marginTop: '16px' },
  card: { background: colors.glass, padding: '18px', borderRadius: '10px', border: `1px solid ${colors.glassBorder}`, cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 5px rgba(0,0,0,0.25)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  cardTitle: { margin: 0, fontSize: '17px', color: colors.text },
  unreadBadge: { background: '#ff6b35', color: 'white', fontSize: '12px', fontWeight: 'bold', padding: '3px 7px', borderRadius: '10px', minWidth: '22px', textAlign: 'center' },
  cardMeta: { display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '12px' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: colors.textMuted },
  emptyIcon: { fontSize: '56px', marginBottom: '16px' },
  clearFilterBtn: { marginTop: '14px', padding: '9px 18px', background: '#2fb65c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  industryTag: { display: 'inline-block', background: 'rgba(58,155,212,0.18)', color: '#8fcdee', border: '1px solid rgba(58,155,212,0.35)', borderRadius: '10px', padding: '3px 9px', fontSize: '11px', fontWeight: '600' }
};

export default ClientList;
